import type { ActionLogEntry } from "@/features/timesheetFlow/timesheetFlowApi";

export type ActionLogDocumentKind = "stundennachweis" | "lieferschein";

export interface ActionLogView {
  badge: string;
  badgeClass: string;
  lines: string[];
  who: string;
}

const GREEN = "bg-status-green/20 text-status-green";
const ACCENT = "bg-accent/20 text-accent";
const NEUTRAL = "bg-fill-tertiary text-label";
const SKY = "bg-sky-500/20 text-sky-800 dark:text-sky-200";
const AMBER = "bg-amber-500/20 text-amber-900 dark:text-amber-100";

function documentName(kind: ActionLogDocumentKind): string {
  return kind === "lieferschein" ? "Lieferschein" : "Stundennachweis";
}

function parseDetails(entry: ActionLogEntry): Record<string, unknown> | null {
  const raw = entry.details;
  if (!raw) return null;
  if (typeof raw === "object") return raw as Record<string, unknown>;
  try {
    return JSON.parse(String(raw)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function textList(value: unknown): string {
  if (Array.isArray(value)) {
    return value.map((item) => String(item).trim()).filter(Boolean).join(", ");
  }
  return "";
}

function detailText(details: Record<string, unknown> | null, ...keys: string[]): string {
  if (!details) return "";
  for (const key of keys) {
    const value = String(details[key] ?? "").trim();
    if (value) return value;
  }
  return "";
}

export function presentActionActor(entry: ActionLogEntry): string {
  const raw = String(entry.created_by_display || entry.created_by_login || entry.created_by || "").trim();
  if (!raw) return "—";
  const lower = raw.toLowerCase();
  if (lower.startsWith("kunde")) return "Kunde";
  if (lower === "public") return "Kunde";
  if (lower === "system" || lower === "scan_import") return "System";
  if (lower === "hero_sync" || lower === "hero sync") return "Hero";
  return raw;
}

function mailLines(intro: string, entry: ActionLogEntry): string[] {
  const lines = [intro];
  const to = textList(entry.recipients);
  lines.push(`An: ${to || "—"}`);
  const subject = String(entry.subject || "").trim();
  if (subject) lines.push(`Betreff: ${subject}`);
  return lines;
}

function view(badge: string, badgeClass: string, who: string, lines: string[]): ActionLogView {
  return { badge, badgeClass, who, lines: lines.filter((line) => line.trim()) };
}

export function presentActionLogEntry(
  entry: ActionLogEntry,
  options: { kind?: ActionLogDocumentKind; number?: string } = {},
): ActionLogView {
  const kind = options.kind ?? "stundennachweis";
  const doc = documentName(kind);
  const number = String(options.number || "").trim();
  const named = number ? `${doc} ${number}` : doc;
  const action = String(entry.action || "").trim();
  const who = presentActionActor(entry);
  const details = parseDetails(entry);

  switch (action) {
    case "created":
      return view("Erstellt", GREEN, who, [`${named} wurde erstellt.`]);
    case "updated":
    case "changed":
      return view("Geändert", NEUTRAL, who, [`${named} wurde geändert.`]);
    case "deleted":
      return view("Gelöscht", NEUTRAL, who, [`${named} wurde gelöscht.`]);
    case "mail":
      return view("Mail", ACCENT, who, mailLines("Per E-Mail verschickt.", entry));
    case "mail_reminder":
      return view("Erinnerung", ACCENT, who, mailLines("Eine Erinnerung wurde per E-Mail verschickt.", entry));
    case "mail_public_signed":
      return view(
        "Mail",
        ACCENT,
        who,
        mailLines("Die unterschriebene PDF wurde per E-Mail verschickt.", entry),
      );
    case "mail_imported":
      return view("Mail", ACCENT, who, ["Die unterschriebene PDF ist per E-Mail eingegangen."]);
    case "print": {
      const copies = Number(entry.copies ?? 1);
      const copyLabel = copies === 1 ? "1 Kopie" : `${Number.isFinite(copies) ? copies : 1} Kopien`;
      const printer = String(entry.printer_name || "").trim();
      return view("Druck", NEUTRAL, who, [
        printer ? `Gedruckt auf ${printer} (${copyLabel}).` : `Gedruckt (${copyLabel}).`,
      ]);
    }
    case "contact_marked":
      return view("Kontakt", SKY, who, ["Beim Ansprechpartner markiert."]);
    case "contact_unmarked":
      return view("Kontakt", SKY, who, ["Markierung beim Ansprechpartner entfernt."]);
    case "signed":
      return view("Unterschrieben", GREEN, who, [`${doc} wurde unterschrieben.`]);
    case "signed_outdated_revision": {
      const signedNumber =
        detailText(details, "signed_display_number", "signed_timesheet_id") || number || "—";
      const latestNumber = detailText(details, "latest_display_number", "latest_timesheet_id") || "—";
      return view("Veraltete Version", AMBER, who, [
        `Nachweis ${signedNumber} wurde unterschrieben, es existiert bereits Version ${latestNumber}.`,
      ]);
    }
    case "invoice_draft":
      return view("Rechnungsentwurf", GREEN, who, ["Rechnungsentwurf wurde erstellt."]);
    case "invoice_draft_cleared":
      return view(
        "Rechnungsentwurf",
        AMBER,
        who,
        [
          detailText(details, "reason") === "gd_rechnung_storniert"
            ? "Die verknüpfte Rechnung wurde storniert. Der Nachweis ist wieder unterschrieben."
            : "Der Rechnungsentwurf wurde entfernt.",
        ],
      );
    case "invoice_draft_from_cancelled":
      return view("Rechnungsentwurf", GREEN, who, [
        "Nach einem Storno wurde ein neuer Rechnungsentwurf zugeordnet.",
      ]);
    case "invoice_linked": {
      const provider = detailText(details, "provider");
      const label = detailText(details, "display_number", "invoice_number");
      const source = provider === "hero" ? "Hero-Rechnung" : "Rechnung";
      return view("Rechnung verknüpft", GREEN, who, [
        label ? `${source} ${label} wurde verknüpft.` : `${source} wurde verknüpft.`,
      ]);
    }
    case "invoice_created":
      return view("Rechnung", GREEN, who, ["Rechnung wurde erstellt."]);
    case "invoice_reassigned": {
      const label = detailText(details, "display_number", "invoice_number");
      return view("Rechnung geändert", AMBER, who, [
        label
          ? `Die verknüpfte Rechnung wurde auf ${label} geändert.`
          : "Die verknüpfte Rechnung wurde geändert.",
      ]);
    }
    case "billing_question_created":
      return view("Rückfrage", SKY, who, ["Eine Rückfrage zur Abrechnung wurde gestellt."]);
    case "billing_question_answered":
      return view("Rückfrage", GREEN, who, ["Eine Rückfrage zur Abrechnung wurde beantwortet."]);
    case "material_appended": {
      const count = Number(details?.count ?? 0);
      return view("Material", NEUTRAL, who, [
        count > 1 ? `${count} Materialpositionen wurden ergänzt.` : "Material wurde ergänzt.",
      ]);
    }
    case "material_removed": {
      const count = Number(details?.count ?? 0);
      return view("Material", NEUTRAL, who, [
        count > 1 ? `${count} Materialpositionen wurden entfernt.` : "Material wurde entfernt.",
      ]);
    }
    case "revision_created": {
      const label = detailText(details, "display_number");
      return view("Neue Version", AMBER, who, [
        label ? `Neue Version ${label} wurde angelegt.` : "Eine neue Version wurde angelegt.",
      ]);
    }
    case "archived":
      return view("Archiv", NEUTRAL, who, [`${doc} wurde archiviert.`]);
    default:
      break;
  }

  if (action.startsWith("unarchived_to_")) {
    return view("Archiv", NEUTRAL, who, [`${doc} wurde aus dem Archiv geholt.`]);
  }

  return view("Vorgang", NEUTRAL, who, [`${named} wurde bearbeitet.`]);
}
